import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  fetchContentConfig,
  fetchAllContentConfig,
  updateContentConfig,
  createContentConfig,
  deleteContentConfig,
  ContentConfig,
  getThumbnailUrl,
  getThumbnailBlobUrl,
  fetchThumbnails,
  uploadThumbnail,
  fetchQrCodes,
  getQrCodeBlobUrl,
  uploadQrCode,
  fetchSystemIcons,
  getSystemIconBlobUrl,
  uploadSystemIcon,
  getMediaBlobUrl,
  fetchMediaFiles,
  uploadMedia,
  fetchAllPlayersWithCustomers,
  PlayerWithCustomer,
  MediaFileInfo,
  fetchAdvertisers,
} from "../api/api";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";

// Globale Konstante zur Steuerung der Sichtbarkeit des Export-Icons
const SHOW_EXPORT_ICON = true;

// Globale Konstante: Schalter "Inhalte" / "Systemelemente" anzeigen und verfügbar machen (standardmäßig true)
const SHOW_CONTENT_SYSTEM_TOGGLE = true;

// Globaler Cache für Thumbnails (persistiert über Komponenten-Mounts hinweg)
const thumbnailCache = new Map<string, string>();
const thumbnailErrorCache = new Set<string>();
const thumbnailLoadAttemptsCache = new Map<string, number>();
const thumbnailLoadingCache = new Set<string>(); // Verhindert gleichzeitige Anfragen für dasselbe Thumbnail

// Globale Refs für Thumbnail-States (werden von ThumbnailListItem verwendet)
// Diese werden in ContentConfigPanel mit den aktuellen States synchronisiert
let thumbnailBlobUrlsRef: React.MutableRefObject<Map<string, string>> | null = null;
let thumbnailImageErrorsRef: React.MutableRefObject<Set<string>> | null = null;
let thumbnailLoadAttemptsRef: React.MutableRefObject<Map<string, number>> | null = null;

// Komponente für Thumbnail-Anzeige
// Komponente für Thumbnail-Item im Auswahldialog
// Lädt das Thumbnail in eigenem useEffect, um Ladelogik aus dem Render-Pfad zu entfernen
const ThumbnailListItem: React.FC<{
  filename: string;
  onSelect: (filename: string) => void;
  onLoad: (filename: string) => Promise<string | null>;
}> = ({ filename, onSelect, onLoad }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = React.useRef(false);

  useEffect(() => {
    // Prüfe zuerst den globalen Cache
    const cachedBlobUrl = thumbnailCache.get(filename);
    if (cachedBlobUrl) {
      setBlobUrl(cachedBlobUrl);
      return;
    }

    // Prüfe, ob bereits ein Fehler im Cache ist
    if (thumbnailErrorCache.has(filename)) {
      return;
    }

    // Prüfe Versuche im Cache
    const cachedAttempts = thumbnailLoadAttemptsCache.get(filename) || 0;
    if (cachedAttempts >= 3) {
      return;
    }

    // Prüfe, ob bereits ein Ladeversuch läuft
    if (thumbnailLoadingCache.has(filename) || loadingRef.current) {
      return;
    }

    // Prüfe lokale States über Refs (um Endlosschleifen zu vermeiden)
    // Nur prüfen, wenn Refs verfügbar sind (werden von ContentConfigPanel gesetzt)
    if (thumbnailBlobUrlsRef) {
      const currentBlobUrls = thumbnailBlobUrlsRef.current;
      if (currentBlobUrls && currentBlobUrls.has(filename)) {
        setBlobUrl(currentBlobUrls.get(filename)!);
        return;
      }
    }

    if (thumbnailImageErrorsRef) {
      const currentErrors = thumbnailImageErrorsRef.current;
      if (currentErrors && currentErrors.has(filename)) {
        return;
      }
    }

    if (thumbnailLoadAttemptsRef) {
      const currentAttempts = thumbnailLoadAttemptsRef.current;
      if (currentAttempts) {
        const attempts = currentAttempts.get(filename) || 0;
        if (attempts >= 3) {
          return;
        }
      }
    }

    // Thumbnail laden
    loadingRef.current = true;
    thumbnailLoadingCache.add(filename);
    setLoading(true);
    onLoad(filename)
      .then((url) => {
        if (url) {
          setBlobUrl(url);
        }
        setLoading(false);
        loadingRef.current = false;
        thumbnailLoadingCache.delete(filename);
      })
      .catch(() => {
        setLoading(false);
        loadingRef.current = false;
        thumbnailLoadingCache.delete(filename);
      });
  }, [filename, onLoad]);

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={() => onSelect(filename)}>
        <ListItemAvatar>
          <Avatar
            src={blobUrl || undefined}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : !blobUrl ? (
              "?"
            ) : null}
          </Avatar>
        </ListItemAvatar>
        <ListItemText 
          primary={filename}
          sx={{ ml: 2 }}
        />
      </ListItemButton>
    </ListItem>
  );
};

// QR-Code-Item im Auswahldialog (lädt Bild per onLoad)
const QrCodeListItem: React.FC<{
  filename: string;
  onSelect: (filename: string) => void;
  onLoad: (filename: string) => Promise<string | null>;
}> = ({ filename, onSelect, onLoad }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    onLoad(filename)
      .then((url) => {
        if (!cancelled && url) setBlobUrl(url);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filename, onLoad]);

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={() => onSelect(filename)}>
        <ListItemAvatar>
          <Avatar src={blobUrl || undefined} variant="rounded" sx={{ width: 56, height: 56 }}>
            {loading ? <CircularProgress size={24} /> : !blobUrl ? "?" : null}
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={filename} sx={{ ml: 2 }} />
      </ListItemButton>
    </ListItem>
  );
};

const ThumbnailImageDisplay: React.FC<{
  filename: string;
  thumbnailBlobUrls: Map<string, string>;
  thumbnailImageErrors: Set<string>;
  thumbnailLoadAttempts: Map<string, number>;
  onLoad: (filename: string) => Promise<string | null>;
  onError: (filename: string) => void;
  t: (key: string) => string;
  isSystem?: boolean;
}> = ({ filename, thumbnailBlobUrls, thumbnailImageErrors, thumbnailLoadAttempts, onLoad, onError, t, isSystem }) => {
  const cacheKey = isSystem ? `system:${filename}` : filename;
  const [loading, setLoading] = useState(true);
  const blobUrl = thumbnailBlobUrls.get(cacheKey);
  const hasError = thumbnailImageErrors.has(cacheKey);
  const attempts = thumbnailLoadAttempts.get(cacheKey) || 0;
  const loadingRef = React.useRef(false);

  useEffect(() => {
    // Prüfe zuerst den globalen Cache (Cache-Key für System-Icons)
    const cachedBlobUrl = thumbnailCache.get(cacheKey);
    if (cachedBlobUrl) {
      if (!blobUrl) {
        onLoad(filename).catch(() => {});
      }
      setLoading(false);
      return;
    }

    if (thumbnailErrorCache.has(cacheKey)) {
      setLoading(false);
      return;
    }

    const cachedAttempts = thumbnailLoadAttemptsCache.get(cacheKey) || 0;
    if (cachedAttempts >= 3) {
      setLoading(false);
      return;
    }

    if (thumbnailLoadingCache.has(cacheKey) || loadingRef.current) {
      return;
    }

    if (filename && !blobUrl && !hasError && attempts < 3) {
      loadingRef.current = true;
      thumbnailLoadingCache.add(cacheKey);
      setLoading(true);
      onLoad(filename)
        .then(() => {
          setLoading(false);
          loadingRef.current = false;
          thumbnailLoadingCache.delete(cacheKey);
        })
        .catch(() => {
          setLoading(false);
          loadingRef.current = false;
          thumbnailLoadingCache.delete(cacheKey);
          if (attempts >= 2) {
            onError(cacheKey);
          }
        });
    } else {
      setLoading(false);
    }
  }, [filename, cacheKey, blobUrl, hasError, attempts, onLoad, onError]);

  if (loading) {
    return (
      <Box sx={{ mt: 1, display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!blobUrl || hasError) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "error.main",
          fontSize: "0.875rem",
          mt: 1
        }}
      >
        {t("contentConfig.noFileFound")}
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={blobUrl}
      alt={filename}
      onError={() => onError(cacheKey)}
      sx={{
        maxWidth: "100%",
        maxHeight: "200px",
        objectFit: "contain",
        border: "1px solid #ccc",
        borderRadius: "4px",
        mt: 1
      }}
    />
  );
};

const ContentConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const [configData, setConfigData] = useState<ContentConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editingData, setEditingData] = useState<ContentConfig | null>(null);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<ContentConfig | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ContentConfig | null>(null);
  const [newEntryData, setNewEntryData] = useState<ContentConfig | null>(null);
  
  // Filter States
  const [filterGultigkeit, setFilterGultigkeit] = useState("");
  const [filterSuperCategory, setFilterSuperCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterMissingThumbnails, setFilterMissingThumbnails] = useState(false);
  const [filterDuplicateEntityNumbers, setFilterDuplicateEntityNumbers] = useState(false);
  const [filterMissingFileName, setFilterMissingFileName] = useState(false);

  // Modus: "content" = Inhalte, "system" = Systemelemente, "ad" = Werbung (Type = ad)
  const [contentMode, setContentMode] = useState<"content" | "system" | "ad">("content");

  // Thumbnail States
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
  const [availableThumbnails, setAvailableThumbnails] = useState<string[]>([]);
  const [availableSystemIcons, setAvailableSystemIcons] = useState<string[]>([]);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [thumbnailImageErrors, setThumbnailImageErrors] = useState<Set<string>>(new Set());
  const [thumbnailBlobUrls, setThumbnailBlobUrls] = useState<Map<string, string>>(new Map());
  const [thumbnailLoadAttempts, setThumbnailLoadAttempts] = useState<Map<string, number>>(new Map());
  const [thumbnailInputValue, setThumbnailInputValue] = useState<string>(""); // Lokaler State für Thumbnail-Eingabe
  const [thumbnailInputValueNewEntry, setThumbnailInputValueNewEntry] = useState<string>(""); // Lokaler State für Thumbnail-Eingabe bei neuen Einträgen

  // Media States
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [availableMediaFiles, setAvailableMediaFiles] = useState<MediaFileInfo[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);

  // Refs für Thumbnail-States (um Endlosschleifen in useEffect zu vermeiden)
  const thumbnailBlobUrlsRefLocal = React.useRef<Map<string, string>>(new Map());
  const thumbnailImageErrorsRefLocal = React.useRef<Set<string>>(new Set());
  const thumbnailLoadAttemptsRefLocal = React.useRef<Map<string, number>>(new Map());

  // Globale Refs setzen (werden von ThumbnailListItem verwendet)
  React.useEffect(() => {
    thumbnailBlobUrlsRef = thumbnailBlobUrlsRefLocal;
    thumbnailImageErrorsRef = thumbnailImageErrorsRefLocal;
    thumbnailLoadAttemptsRef = thumbnailLoadAttemptsRefLocal;
  }, []);

  // Refs mit States synchronisieren
  React.useEffect(() => {
    thumbnailBlobUrlsRefLocal.current = thumbnailBlobUrls;
  }, [thumbnailBlobUrls]);

  React.useEffect(() => {
    thumbnailImageErrorsRefLocal.current = thumbnailImageErrors;
  }, [thumbnailImageErrors]);

  React.useEffect(() => {
    thumbnailLoadAttemptsRefLocal.current = thumbnailLoadAttempts;
  }, [thumbnailLoadAttempts]);

  // Media States
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerUrl, setMediaViewerUrl] = useState<string | null>(null);
  const [mediaViewerType, setMediaViewerType] = useState<'video' | 'audio' | 'image' | null>(null);
  const [mediaLoadAttempts, setMediaLoadAttempts] = useState<Map<string, number>>(new Map());

  // QR-Code States (nur Inhalte-Modus)
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [availableQrCodes, setAvailableQrCodes] = useState<string[]>([]);
  const [qrCodeInputValue, setQrCodeInputValue] = useState<string>("");
  const [qrCodeInputValueNewEntry, setQrCodeInputValueNewEntry] = useState<string>("");
  const [qrCodeBlobUrls, setQrCodeBlobUrls] = useState<Map<string, string>>(new Map());
  const [generatedQrBlob, setGeneratedQrBlob] = useState<Blob | null>(null);
  const [generatedQrPreviewUrl, setGeneratedQrPreviewUrl] = useState<string | null>(null);

  // Object-URL für generierten QR-Code erstellen/revoken
  React.useEffect(() => {
    if (!generatedQrBlob) {
      setGeneratedQrPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(generatedQrBlob);
    setGeneratedQrPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [generatedQrBlob]);

  // Player States
  const [playersWithCustomers, setPlayersWithCustomers] = useState<PlayerWithCustomer[]>([]);

  // Advertisers (für Werbung-Modus: Advertiser-Dropdown)
  const [advertisersList, setAdvertisersList] = useState<{ id: number; name: string }[]>([]);

  // Konstante für "Für alle Player"
  const FOR_ALL_PLAYERS_KEY = "__FOR_ALL_PLAYERS__";

  // Map für schnellen Zugriff auf Kundenname nach Player-SN
  const playerCustomerMap = useMemo(() => {
    const map = new Map<string, string>();
    playersWithCustomers.forEach(player => {
      map.set(player.sn, player.customer_name);
    });
    return map;
  }, [playersWithCustomers]);

  // Berechne "Gültigkeit" für einen Eintrag (intern)
  const getGultigkeit = (entry: ContentConfig): string => {
    return entry.serial_number === null || entry.serial_number === undefined || entry.serial_number === ""
      ? FOR_ALL_PLAYERS_KEY
      : String(entry.serial_number);
  };

  // Zeige "Gültigkeit" für einen Eintrag an (mit Übersetzung und Kundenname)
  const getGultigkeitDisplay = (gultigkeit: string): string => {
    if (gultigkeit === FOR_ALL_PLAYERS_KEY) {
      return t("contentConfig.forAllPlayers");
    }
    const customerName = playerCustomerMap.get(gultigkeit);
    return customerName ? `${gultigkeit} (${customerName})` : gultigkeit;
  };

  // Prüfe, ob Thumbnail bzw. System-Icon fehlt
  const isThumbnailMissing = useCallback((entry: ContentConfig): boolean => {
    if (!entry.thumbnail) {
      return true; // Kein Thumbnail-/Icon-Wert vorhanden
    }
    const thumbnailName = String(entry.thumbnail);
    if (contentMode === "system") {
      // System-Modus: Einträge ohne System-Icon filtern
      if (!availableSystemIcons.includes(thumbnailName)) {
        return true; // System-Icon nicht in der Liste (fehlt auf dem Server)
      }
      // System-Icon konnte nicht geladen werden (Fehler-Cache-Key: system:filename)
      if (thumbnailImageErrors.has(`system:${thumbnailName}`)) {
        return true;
      }
      return false;
    }
    // Content-Modus: Einträge ohne Thumbnail filtern
    if (!availableThumbnails.includes(thumbnailName)) {
      return true; // Thumbnail nicht in der Liste
    }
    if (thumbnailImageErrors.has(thumbnailName)) {
      return true; // Thumbnail konnte nicht geladen werden
    }
    return false;
  }, [contentMode, availableThumbnails, availableSystemIcons, thumbnailImageErrors]);

  // Zähle Vorkommen von entity_number-Werten (muss vor getFilteredDataWithoutFilter definiert werden)
  const entityNumberCounts = useMemo(() => {
    const counts = new Map<string | number, number>();
    configData.forEach((entry) => {
      if (entry.entity_number !== null && entry.entity_number !== undefined) {
        const key = String(entry.entity_number);
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    });
    return counts;
  }, [configData]);

  // Hilfsfunktion: Filtere Daten mit allen Filtern außer einem bestimmten Filter
  const getFilteredDataWithoutFilter = useCallback((
    excludeFilter: 'gultigkeit' | 'superCategory' | 'category'
  ): ContentConfig[] => {
    return configData.filter((entry) => {
      // Nach Modus filtern: content, system oder ad
      const entryType = entry.type === undefined || entry.type === null ? "content" : String(entry.type);
      if (contentMode === "ad" ? entryType !== "ad" : entryType !== contentMode) {
        return false;
      }
      
      const gultigkeit = getGultigkeit(entry);
      
      // Filter für Gültigkeit (nur wenn nicht ausgeschlossen)
      if (excludeFilter !== 'gultigkeit' && filterGultigkeit) {
        const filterValue = filterGultigkeit === t("contentConfig.forAllPlayers") 
          ? FOR_ALL_PLAYERS_KEY 
          : filterGultigkeit;
        if (gultigkeit !== filterValue) {
          return false;
        }
      }
      
      // Filter für Super Category (nur wenn nicht ausgeschlossen)
      if (excludeFilter !== 'superCategory' && filterSuperCategory && String(entry.super_category || "") !== filterSuperCategory) {
        return false;
      }
      
      // Filter für Category (nur wenn nicht ausgeschlossen)
      if (excludeFilter !== 'category' && filterCategory && String(entry.category || "") !== filterCategory) {
        return false;
      }
      
      // Filter für fehlende Thumbnails (entfällt im Werbung-Modus)
      if (contentMode !== "ad" && filterMissingThumbnails && !isThumbnailMissing(entry)) {
        return false;
      }
      
      // Filter für Mehrfachdatensätze (immer anwenden)
      if (filterDuplicateEntityNumbers) {
        if (entry.entity_number === null || entry.entity_number === undefined) {
          return false; // Einträge ohne entity_number ausschließen
        }
        const entityNumberKey = String(entry.entity_number);
        const count = entityNumberCounts.get(entityNumberKey) || 0;
        if (count <= 1) {
          return false; // Nur Einträge mit mehr als einem Vorkommen anzeigen
        }
      }
      
      // Filter für fehlende Dateinamen (immer anwenden)
      if (filterMissingFileName) {
        if (entry.file_name && entry.file_name.trim() !== "") {
          return false; // Nur Einträge ohne file_name anzeigen
        }
      }
      
      return true;
    });
  }, [configData, contentMode, filterGultigkeit, filterSuperCategory, filterCategory, filterMissingThumbnails, filterDuplicateEntityNumbers, filterMissingFileName, isThumbnailMissing, entityNumberCounts, t]);

  // Eindeutige Filterwerte extrahieren - nur aus aktuell angezeigten Datensätzen (ohne den jeweiligen Filter)
  const filterValues = useMemo(() => {
    // Für Gültigkeit: Filtere nach superCategory, category und missingThumbnails
    const dataForGultigkeit = getFilteredDataWithoutFilter('gultigkeit');
    const gultigkeitValues = new Set<string>();
    dataForGultigkeit.forEach((entry) => {
      const gultigkeit = getGultigkeit(entry);
      gultigkeitValues.add(gultigkeit);
    });

    // Für Super Category: Filtere nach gultigkeit, category und missingThumbnails
    const dataForSuperCategory = getFilteredDataWithoutFilter('superCategory');
    const superCategoryValues = new Set<string>();
    dataForSuperCategory.forEach((entry) => {
      if (entry.super_category !== null && entry.super_category !== undefined && entry.super_category !== "") {
        superCategoryValues.add(String(entry.super_category));
      }
    });

    // Für Category: Filtere nach gultigkeit, superCategory und missingThumbnails
    const dataForCategory = getFilteredDataWithoutFilter('category');
    const categoryValues = new Set<string>();
    dataForCategory.forEach((entry) => {
      if (entry.category !== null && entry.category !== undefined && entry.category !== "") {
        categoryValues.add(String(entry.category));
      }
    });

    return {
      gultigkeit: Array.from(gultigkeitValues).sort(),
      superCategory: Array.from(superCategoryValues).sort(),
      category: Array.from(categoryValues).sort(),
    };
  }, [getFilteredDataWithoutFilter]);

  // Gefilterte Daten nach Modus: content, system oder ad
  const filteredData = useMemo(() => {
    const filtered = configData.filter((entry) => {
      const entryType = entry.type === undefined || entry.type === null ? "content" : String(entry.type);
      if (contentMode === "ad" ? entryType !== "ad" : entryType !== contentMode) {
        return false;
      }
      
      const gultigkeit = getGultigkeit(entry);
      
      if (filterGultigkeit) {
        // Prüfe sowohl den internen Wert als auch die Übersetzung
        const filterValue = filterGultigkeit === t("contentConfig.forAllPlayers") 
          ? FOR_ALL_PLAYERS_KEY 
          : filterGultigkeit;
        if (gultigkeit !== filterValue) {
          return false;
        }
      }
      if (filterSuperCategory && String(entry.super_category || "") !== filterSuperCategory) {
        return false;
      }
      if (filterCategory && String(entry.category || "") !== filterCategory) {
        return false;
      }
      
      // Filter für fehlende Thumbnails (entfällt im Werbung-Modus)
      if (contentMode !== "ad" && filterMissingThumbnails && !isThumbnailMissing(entry)) {
        return false;
      }
      
      // Filter für Mehrfachdatensätze (entity_number kommt mehr als einmal vor)
      if (filterDuplicateEntityNumbers) {
        if (entry.entity_number === null || entry.entity_number === undefined) {
          return false; // Einträge ohne entity_number ausschließen
        }
        const entityNumberKey = String(entry.entity_number);
        const count = entityNumberCounts.get(entityNumberKey) || 0;
        if (count <= 1) {
          return false; // Nur Einträge mit mehr als einem Vorkommen anzeigen
        }
      }
      
      // Filter für fehlende Dateinamen
      if (filterMissingFileName) {
        if (entry.file_name && entry.file_name.trim() !== "") {
          return false; // Nur Einträge ohne file_name anzeigen
        }
      }
      
      return true;
    });
    
    // Nach entity_number aufsteigend sortieren
    return filtered.sort((a, b) => {
      const aNum = a.entity_number !== null && a.entity_number !== undefined ? Number(a.entity_number) : Infinity;
      const bNum = b.entity_number !== null && b.entity_number !== undefined ? Number(b.entity_number) : Infinity;
      return aNum - bNum;
    });
  }, [configData, contentMode, filterGultigkeit, filterSuperCategory, filterCategory, filterMissingThumbnails, filterDuplicateEntityNumbers, filterMissingFileName, isThumbnailMissing, entityNumberCounts, t]);

  // Lade Konfigurationsdaten (bei aktivem Inhalte/Systemelemente-Schalter alle Typen, sonst nur content)
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = SHOW_CONTENT_SYSTEM_TOGGLE ? await fetchAllContentConfig() : await fetchContentConfig();
      setConfigData(data);
      
      // Verfügbare Thumbnails laden
      try {
        const thumbnails = await fetchThumbnails();
        setAvailableThumbnails(thumbnails);
      } catch (thumbError) {
        console.error("Fehler beim Laden der Thumbnail-Liste:", thumbError);
      }
    } catch (error: any) {
      console.error("Fehler beim Laden der Konfiguration:", error);
      const errorMessage = error?.data?.detail || error?.data?.message || error?.message || t("contentConfig.errorLoading");
      setSnackbarMsg(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Einheitliche Fetch-Funktion nach Mutationen (mit oder ohne alle Typen)
  const fetchConfigAfterMutation = useCallback(() => {
    return SHOW_CONTENT_SYSTEM_TOGGLE ? fetchAllContentConfig() : fetchContentConfig();
  }, []);

  // Lade Player mit Kundeninformationen
  const loadPlayers = useCallback(async () => {
    try {
      const players = await fetchAllPlayersWithCustomers();
      setPlayersWithCustomers(players);
    } catch (error: any) {
      console.error("Fehler beim Laden der Player:", error);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadPlayers();
  }, [loadConfig, loadPlayers]);

  // System-Icon-Liste laden, wenn System-Modus aktiv (für Filter "Einträge ohne System-Icon")
  useEffect(() => {
    if (contentMode !== "system") return;
    let cancelled = false;
    fetchSystemIcons()
      .then((list) => {
        if (!cancelled) setAvailableSystemIcons(list);
      })
      .catch((err) => {
        if (!cancelled) console.error("Fehler beim Laden der System-Icons:", err);
      });
    return () => { cancelled = true; };
  }, [contentMode]);

  // Advertiser-Liste laden, wenn Werbung-Modus aktiv (für Advertiser-Dropdown)
  useEffect(() => {
    if (contentMode !== "ad") return;
    let cancelled = false;
    fetchAdvertisers()
      .then((list) => {
        if (!cancelled) setAdvertisersList(list.map((a) => ({ id: a.id, name: a.name })));
      })
      .catch((err) => {
        if (!cancelled) console.error("Fehler beim Laden der Advertiser:", err);
      });
    return () => { cancelled = true; };
  }, [contentMode]);

  // Hilfsfunktion: Eintrag eindeutig identifizieren (id oder entity_number + serial_number)
  const findEntryByIdentifier = (data: ContentConfig[], identifier: { id?: number | string | null, entity_number?: number | string | null, serial_number?: string | null }) => {
    return data.find(e => {
      // Wenn id vorhanden ist, verwende id
      if (identifier.id !== undefined && identifier.id !== null && e.id !== undefined && e.id !== null && e.id === identifier.id) {
        return true;
      }
      // Sonst verwende entity_number + serial_number
      if (identifier.entity_number !== undefined && identifier.entity_number !== null && 
          e.entity_number !== undefined && e.entity_number !== null && 
          String(e.entity_number) === String(identifier.entity_number)) {
        const identifierSerial = identifier.serial_number === null || identifier.serial_number === undefined ? null : String(identifier.serial_number);
        const entrySerial = e.serial_number === null || e.serial_number === undefined ? null : String(e.serial_number);
        return identifierSerial === entrySerial;
      }
      return false;
    });
  };

  // Bearbeitung starten
  const handleEdit = () => {
    if (!selectedEntry) {
      console.error("Fehler: selectedEntry fehlt");
      return;
    }
    
    // id wird als primärer Schlüssel verwendet
    if (selectedEntry.id === undefined || selectedEntry.id === null) {
      console.error("Fehler: selectedEntry.id fehlt", { selectedEntry, keys: Object.keys(selectedEntry) });
      setSnackbarMsg(t("contentConfig.errorMissingId"));
      return;
    }
    
    setEditingId(selectedEntry.id);
    setEditingData({ ...selectedEntry });
    // Lokalen Thumbnail-Input-State initialisieren
    setThumbnailInputValue(selectedEntry.thumbnail ? String(selectedEntry.thumbnail) : "");
    setQrCodeInputValue(selectedEntry.qr_code ? String(selectedEntry.qr_code) : "");
    setGeneratedQrBlob(null);
  };

  // Eintrag duplizieren
  const handleDuplicate = () => {
    if (!selectedEntry) {
      console.error("Fehler: selectedEntry fehlt beim Duplizieren");
      return;
    }
    
    // Alle Attribute außer id kopieren (inkl. entity_number)
    const { id, ...duplicatedData } = selectedEntry;
    
    // Duplizierten Eintrag erstellen (entity_number wird mitkopiert)
    const duplicatedEntry: ContentConfig = {
      ...duplicatedData,
      // Sicherstellen, dass type gesetzt ist
      type: duplicatedData.type || "content",
    };
    
    // Als neuen Eintrag setzen (ähnlich wie handleCreateNew)
    setNewEntryData(duplicatedEntry);
    setSelectedEntry(null);
    setEditingId(null);
    setEditingData(null);
    // Lokalen Thumbnail-Input-State initialisieren
    setThumbnailInputValue(duplicatedEntry.thumbnail ? String(duplicatedEntry.thumbnail) : "");
    setThumbnailInputValueNewEntry(duplicatedEntry.thumbnail ? String(duplicatedEntry.thumbnail) : "");
    setQrCodeInputValue(duplicatedEntry.qr_code ? String(duplicatedEntry.qr_code) : "");
    setQrCodeInputValueNewEntry(duplicatedEntry.qr_code ? String(duplicatedEntry.qr_code) : "");
    setGeneratedQrBlob(null);
  };

  // Bearbeitung abbrechen
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
    setThumbnailInputValue(""); // Lokalen Thumbnail-Input-State zurücksetzen
    setThumbnailInputValueNewEntry(""); // Lokalen Thumbnail-Input-State für neue Einträge zurücksetzen
    setQrCodeInputValue("");
    setQrCodeInputValueNewEntry("");
    setGeneratedQrBlob(null);
    // Zurück zur ursprünglichen Auswahl
    if (selectedEntry) {
      const searchIdentifier = {
        id: selectedEntry.id,
        entity_number: selectedEntry.entity_number,
        serial_number: selectedEntry.serial_number
      };
      const updatedEntry = findEntryByIdentifier(configData, searchIdentifier);
      if (updatedEntry) {
        setSelectedEntry(updatedEntry);
      }
    }
  };

  // Änderungen speichern
  const handleSave = async () => {
    if (!editingId || !editingData) {
      console.error("Fehler: editingId oder editingData fehlt", { editingId, editingData });
      return;
    }

    try {
      // Generierten QR-Code zuerst hochladen, falls vorhanden
      if (generatedQrBlob && editingData.qr_code) {
        const filename = String(editingData.qr_code).trim();
        const file = new File([generatedQrBlob], filename, { type: "image/png" });
        await uploadQrCode(file);
      }

      // id und entity_number aus editingData entfernen für Update
      const { id, entity_number, ...updateData } = editingData;
      
      // serial_number normalisieren: leere Strings zu null
      if (updateData.serial_number !== undefined) {
        updateData.serial_number = (updateData.serial_number === null || updateData.serial_number === undefined || updateData.serial_number === '') 
          ? null 
          : updateData.serial_number;
      }
      
      // id wird als primärer Schlüssel verwendet
      await updateContentConfig(editingId as number, updateData);
      setGeneratedQrBlob(null);
      setSnackbarMsg(t("contentConfig.configSaved"));
      setEditingId(null);
      setEditingData(null);
      setThumbnailInputValue(""); // Lokalen Thumbnail-Input-State zurücksetzen
      // Verwende id oder entity_number + serial_number zur Wiederfindung
      const previousSelectedIdentifier = selectedEntry ? {
        id: selectedEntry.id,
        entity_number: selectedEntry.entity_number,
        serial_number: selectedEntry.serial_number
      } : null;
      // Daten neu laden
      const updatedData = await fetchConfigAfterMutation();
      setConfigData(updatedData);
      // Verfügbare Thumbnails aktualisieren
      try {
        const thumbnails = await fetchThumbnails();
        setAvailableThumbnails(thumbnails);
      } catch (thumbError) {
        console.error("Fehler beim Aktualisieren der Thumbnail-Liste:", thumbError);
      }
      // Selektion nach dem Neuladen beibehalten
      if (previousSelectedIdentifier) {
        const updatedEntry = findEntryByIdentifier(updatedData, previousSelectedIdentifier);
        if (updatedEntry) {
          setSelectedEntry(updatedEntry);
        }
      }
    } catch (error: any) {
      console.error("Fehler beim Speichern:", error);
      
      // Spezifische Fehlermeldung vom Server anzeigen, falls vorhanden
      if (error?.status === 409) {
        // Constraint-Verletzung: Übersetzung basierend auf errorCode verwenden
        const errorCode = error?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`contentConfig.errors.${errorCode}`, { defaultValue: error?.data?.message || error?.message || t("contentConfig.errorSaving") })
          : (error?.data?.message || error?.message || t("contentConfig.errorSaving"));
        setSnackbarMsg(errorMessage);
      } else {
        // Andere Fehler: Standard-Fehlermeldung
        const errorCode = error?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`contentConfig.errors.${errorCode}`, { defaultValue: error?.data?.message || error?.message || t("contentConfig.errorSaving") })
          : (error?.data?.message || error?.message || t("contentConfig.errorSaving"));
        setSnackbarMsg(errorMessage);
      }
    }
  };

  // Löschen bestätigen
  const handleDeleteClick = () => {
    if (!selectedEntry) {
      console.error("Fehler: selectedEntry fehlt beim Löschen");
      return;
    }
    
    // id wird als primärer Schlüssel verwendet
    if (selectedEntry.id === undefined || selectedEntry.id === null) {
      console.error("Fehler: selectedEntry.id fehlt beim Löschen", { selectedEntry, keys: Object.keys(selectedEntry) });
      setSnackbarMsg(t("contentConfig.errorMissingId"));
      return;
    }
    
    setEntryToDelete(selectedEntry);
    setDeleteDialogOpen(true);
  };

  // Löschen durchführen
  const handleConfirmDelete = async () => {
    if (!entryToDelete) {
      console.error("Fehler: Kein Eintrag zum Löschen gefunden");
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      return;
    }

    // id wird als primärer Schlüssel verwendet
    if (entryToDelete.id === undefined || entryToDelete.id === null) {
      console.error("Fehler: Kein gültiger Eintrag zum Löschen gefunden (id fehlt)");
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      setSnackbarMsg(t("contentConfig.errorMissingId"));
      return;
    }

    try {
      await deleteContentConfig(entryToDelete.id);
      setSnackbarMsg(t("contentConfig.configSaved"));
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      setSelectedEntry(null);
      await loadConfig();
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      setSnackbarMsg(t("contentConfig.errorSaving"));
    }
  };

  // Feldwert ändern (für Bearbeitung)
  const handleFieldChange = (field: string, value: any) => {
    if (editingData) {
      setEditingData({ ...editingData, [field]: value });
    }
  };

  // Handle row click for selection
  const handleRowClick = (entry: ContentConfig) => {
    setSelectedEntry(entry);
    setEditingId(null);
    setEditingData(null);
    setNewEntryData(null);
    setThumbnailInputValue(""); // Lokalen Thumbnail-Input-State zurücksetzen
    setThumbnailInputValueNewEntry(""); // Lokalen Thumbnail-Input-State für neue Einträge zurücksetzen
    setQrCodeInputValue("");
    setQrCodeInputValueNewEntry("");
    setGeneratedQrBlob(null);
  };

  // Nächste freie entity_number berechnen
  const getNextEntityNumber = (): string => {
    const existingNumbers = configData
      .map(entry => entry.entity_number)
      .filter(num => num !== null && num !== undefined && num !== "")
      .map(num => {
        const parsed = parseInt(String(num), 10);
        return isNaN(parsed) ? 0 : parsed;
      });
    
    if (existingNumbers.length === 0) {
      return "1";
    }
    
    const maxNumber = Math.max(...existingNumbers);
    // Finde die erste freie Nummer ab 1
    for (let i = 1; i <= maxNumber + 1; i++) {
      if (!existingNumbers.includes(i)) {
        return String(i);
      }
    }
    return String(maxNumber + 1);
  };

  // Neuen Eintrag erstellen (type je nach Modus: content, system oder ad)
  const handleCreateNew = () => {
    const nextEntityNumber = getNextEntityNumber();
    const newEntry: ContentConfig = {
      entity_number: nextEntityNumber,
      type: contentMode,
      serial_number: null, // Explizit auf null setzen für "Für alle Player"
      // landscape wird vom Server gesetzt
    };
    setNewEntryData(newEntry);
    setSelectedEntry(null);
    setEditingId(null);
    setEditingData(null);
    setThumbnailInputValue(""); // Lokalen Thumbnail-Input-State zurücksetzen
    setThumbnailInputValueNewEntry(""); // Lokalen Thumbnail-Input-State für neue Einträge zurücksetzen
    setQrCodeInputValue("");
    setQrCodeInputValueNewEntry("");
    setGeneratedQrBlob(null);
  };

  // QR-Code-Dialog öffnen (Liste laden)
  const handleOpenQrCodeDialog = async () => {
    setQrCodeDialogOpen(true);
    try {
      const list = await fetchQrCodes();
      setAvailableQrCodes(list);
    } catch (error: any) {
      console.error("Fehler beim Laden der QR-Codes:", error);
      setSnackbarMsg(error?.message || t("contentConfig.qrCodeGenerateError"));
    }
  };

  // QR-Code aus Liste auswählen
  const handleSelectQrCode = (filename: string) => {
    if (newEntryData) {
      setNewEntryData({ ...newEntryData, qr_code: filename });
      setQrCodeInputValueNewEntry(filename);
    } else {
      handleFieldChange("qr_code", filename);
      setQrCodeInputValue(filename);
    }
    setQrCodeDialogOpen(false);
  };

  // QR-Code-Datei hochladen
  const handleQrCodeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadQrCode(file);
      if (newEntryData) {
        setNewEntryData({ ...newEntryData, qr_code: result.filename });
        setQrCodeInputValueNewEntry(result.filename);
      } else {
        handleFieldChange("qr_code", result.filename);
        setQrCodeInputValue(result.filename);
      }
      setSnackbarMsg(t("contentConfig.qrCodeUploaded"));
      const list = await fetchQrCodes();
      setAvailableQrCodes(list);
    } catch (error: any) {
      setSnackbarMsg(error?.message || t("contentConfig.qrCodeGenerateError"));
    }
    event.target.value = "";
  };

  // QR-Code aus Link erzeugen (wird beim Speichern hochgeladen)
  const handleGenerateQrCode = async () => {
    const currentData = newEntryData || editingData;
    const link = currentData?.qr_code_link;
    const trimmed = link !== null && link !== undefined ? String(link).trim() : "";
    if (!trimmed) {
      setSnackbarMsg(t("contentConfig.qrCodeLinkRequired"));
      return;
    }
    try {
      const dataUrl = await QRCode.toDataURL(trimmed, { type: "image/png", margin: 1 });
      const currentQrCode = currentData?.qr_code;
      const qrCodeEmpty = currentQrCode === null || currentQrCode === undefined || String(currentQrCode).trim() === "";
      const nameVal = currentData?.name;
      const baseName = (nameVal !== null && nameVal !== undefined && String(nameVal).trim() !== "")
        ? String(nameVal).trim().replace(/[^a-zA-Z0-9_\u00C0-\u024F-]/g, "_")
        : "qr";
      const filename = qrCodeEmpty ? `${baseName}_qr.png` : String(currentQrCode).trim();
      if (qrCodeEmpty) {
        if (newEntryData) {
          setNewEntryData({ ...newEntryData, qr_code: filename });
          setQrCodeInputValueNewEntry(filename);
        } else {
          handleFieldChange("qr_code", filename);
          setQrCodeInputValue(filename);
        }
      }
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      setGeneratedQrBlob(blob);
      setSnackbarMsg(t("contentConfig.qrCodeGenerated"));
    } catch (error: any) {
      setSnackbarMsg(error?.message || t("contentConfig.qrCodeGenerateError"));
    }
  };

  // Neuen Eintrag speichern
  const handleSaveNew = async () => {
    if (!newEntryData) return;

    try {
      // Generierten QR-Code zuerst hochladen, falls vorhanden
      if (generatedQrBlob && newEntryData.qr_code) {
        const filename = String(newEntryData.qr_code).trim();
        const file = new File([generatedQrBlob], filename, { type: "image/png" });
        await uploadQrCode(file);
      }

      // Sicherstellen, dass serial_number explizit gesetzt ist (auch wenn null)
      const dataToSave = {
        ...newEntryData,
        serial_number: newEntryData.serial_number !== undefined ? newEntryData.serial_number : null
      };
      const created = await createContentConfig(dataToSave);
      setGeneratedQrBlob(null);
      setSnackbarMsg(t("contentConfig.configSaved"));
      setNewEntryData(null);
      setThumbnailInputValueNewEntry(""); // Lokalen Thumbnail-Input-State für neue Einträge zurücksetzen
      // WICHTIG: Selektion auf null setzen, damit der ursprüngliche Eintrag nicht mehr ausgewählt ist
      setSelectedEntry(null);
      // Verwende id oder entity_number + serial_number zur Wiederfindung
      const createdIdentifier = {
        id: created.id,
        entity_number: created.entity_number,
        serial_number: created.serial_number
      };
      // Daten neu laden
      const updatedData = await fetchConfigAfterMutation();
      setConfigData(updatedData);
      // Neuen Eintrag nach dem Laden auswählen (sollte immer funktionieren, da id eindeutig ist)
      const updatedEntry = findEntryByIdentifier(updatedData, createdIdentifier);
      if (updatedEntry) {
        setSelectedEntry(updatedEntry);
      } else {
        // Fallback: Versuche anhand von entity_number + serial_number zu finden
        console.warn("Eintrag mit id nicht gefunden, versuche Fallback-Suche");
        const fallbackEntry = updatedData.find(e => 
          e.id === created.id || 
          (e.entity_number === created.entity_number && 
           (e.serial_number === created.serial_number || 
            (e.serial_number === null && created.serial_number === null)))
        );
        if (fallbackEntry) {
          setSelectedEntry(fallbackEntry);
        }
      }
    } catch (error: any) {
      console.error("Fehler beim Erstellen:", error);
      
      // Spezifische Fehlermeldung vom Server anzeigen, falls vorhanden
      if (error?.status === 409) {
        // Constraint-Verletzung: Übersetzung basierend auf errorCode verwenden
        const errorCode = error?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`contentConfig.errors.${errorCode}`, { defaultValue: error?.data?.message || error?.message || t("contentConfig.errorSaving") })
          : (error?.data?.message || error?.message || t("contentConfig.errorSaving"));
        setSnackbarMsg(errorMessage);
      } else {
        // Andere Fehler: Standard-Fehlermeldung
        const errorCode = error?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`contentConfig.errors.${errorCode}`, { defaultValue: error?.data?.message || error?.message || t("contentConfig.errorSaving") })
          : (error?.data?.message || error?.message || t("contentConfig.errorSaving"));
        setSnackbarMsg(errorMessage);
      }
    }
  };

  // Neuen Eintrag abbrechen
  const handleCancelNew = () => {
    setNewEntryData(null);
  };

  // JSON-Export-Funktion
  const handleExportToJSON = async () => {
    try {
      // Lade alle Daten aus der Datenbanktabelle rc_config
      const allData = await fetchAllContentConfig();
      
      if (allData.length === 0) {
        setSnackbarMsg(t("contentConfig.noDataToExport") || "Keine Daten zum Exportieren vorhanden");
        return;
      }

      // Filtere landscape aus den Einträgen heraus
      const dataToExport = allData.map(entry => {
        const { landscape, ...entryWithoutLandscape } = entry;
        return entryWithoutLandscape;
      });

      // Konvertiere zu JSON-String
      const jsonContent = JSON.stringify(dataToExport, null, 2);

      // Erstelle Blob und download
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'contents.json');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbarMsg(t("contentConfig.exportSuccess") || "Daten erfolgreich exportiert");
    } catch (error: any) {
      console.error("Fehler beim Exportieren der Daten:", error);
      setSnackbarMsg(t("contentConfig.exportError") || "Fehler beim Exportieren der Daten");
    }
  };

  // Thumbnail- oder System-Icon-Dialog öffnen (im System-Modus: SYSTEM_ICON_DIR)
  const handleOpenThumbnailDialog = async () => {
    setThumbnailDialogOpen(true);
    setThumbnailLoading(true);
    setThumbnailError(null);
    try {
      const list = contentMode === "system" ? await fetchSystemIcons() : await fetchThumbnails();
      setAvailableThumbnails(list);
      if (contentMode === "system") setAvailableSystemIcons(list);
    } catch (error: any) {
      console.error(contentMode === "system" ? "Fehler beim Laden der System-Icons:" : "Fehler beim Laden der Thumbnails:", error);
      setThumbnailError(error.message || (contentMode === "system" ? "Fehler beim Laden der System-Icons" : "Fehler beim Laden der Thumbnails"));
    } finally {
      setThumbnailLoading(false);
    }
  };

  // Thumbnail auswählen
  const handleSelectThumbnail = (filename: string) => {
    if (newEntryData) {
      setNewEntryData({ ...newEntryData, thumbnail: filename });
      // Aktualisiere auch den lokalen State für die Anzeige im TextField
      setThumbnailInputValueNewEntry(filename);
    } else if (editingId !== null && selectedEntry) {
      // Im Bearbeitungsmodus: Aktualisiere editingData und thumbnailInputValue
      handleFieldChange('thumbnail', filename);
      // Aktualisiere auch den lokalen State für die Anzeige im TextField
      setThumbnailInputValue(filename);
    }
    setThumbnailDialogOpen(false);
  };

  // Thumbnail oder System-Icon hochladen (im System-Modus: SYSTEM_ICON_DIR)
  const handleThumbnailUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = contentMode === "system"
        ? await uploadSystemIcon(file)
        : await uploadThumbnail(file);
      if (newEntryData) {
        setNewEntryData({ ...newEntryData, thumbnail: result.filename });
        setThumbnailInputValueNewEntry(result.filename);
      } else if (editingId !== null && selectedEntry) {
        handleFieldChange('thumbnail', result.filename);
        setThumbnailInputValue(result.filename);
      }
      setSnackbarMsg(t("contentConfig.thumbnailUploaded"));
      const list = contentMode === "system" ? await fetchSystemIcons() : await fetchThumbnails();
      setAvailableThumbnails(list);
      if (contentMode === "system") setAvailableSystemIcons(list);
    } catch (error: any) {
      console.error(contentMode === "system" ? "Fehler beim Hochladen des System-Icons:" : "Fehler beim Hochladen:", error);
      setSnackbarMsg(error.message || t("contentConfig.thumbnailUploadError"));
    }
    event.target.value = '';
  };

  // Media-Datei auswählen (Dialog öffnen)
  const handleOpenMediaDialog = async () => {
    setMediaDialogOpen(true);
    setMediaLoading(true);
    setMediaError(null);
    
    try {
      const files = await fetchMediaFiles(contentMode === 'ad' ? { dir: 'ads' } : undefined);
      setAvailableMediaFiles(files);
    } catch (error: any) {
      console.error("Fehler beim Laden der Media-Dateien:", error);
      setMediaError(error.message || t("contentConfig.mediaLoadError"));
    } finally {
      setMediaLoading(false);
    }
  };

  // Media-Datei aus Dialog auswählen
  const handleSelectMedia = (filename: string) => {
    if (newEntryData) {
      setNewEntryData({ ...newEntryData, file_name: filename });
    } else if (editingData) {
      handleFieldChange('file_name', filename);
    }
    setMediaDialogOpen(false);
  };

  // Hilfsfunktion: Dateigröße formatieren
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Media-Datei hochladen
  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await uploadMedia(file, contentMode === 'ad' ? { dir: 'ads' } : undefined);
      // file_name auf den hochgeladenen Dateinamen setzen
      if (newEntryData) {
        setNewEntryData({ ...newEntryData, file_name: result.filename });
      } else if (editingData) {
        handleFieldChange('file_name', result.filename);
      }
      setSnackbarMsg(t("contentConfig.mediaUploaded"));
    } catch (error: any) {
      console.error("Fehler beim Hochladen:", error);
      setSnackbarMsg(error.message || t("contentConfig.mediaUploadError"));
    }
    // Input zurücksetzen, damit derselbe Datei erneut ausgewählt werden kann
    event.target.value = '';
  };

  // Media-Datei anzeigen
  const handleViewMedia = async (filename: string) => {
    console.log(`[ContentConfigPanel] handleViewMedia aufgerufen mit filename: ${filename}`);
    
    // Prüfen, ob bereits zu viele Versuche unternommen wurden
    const attempts = mediaLoadAttempts.get(filename) || 0;
    if (attempts >= 3) {
      setSnackbarMsg(t("contentConfig.mediaLoadError"));
      return;
    }
    
    // Versuch zählen
    setMediaLoadAttempts(prev => new Map(prev).set(filename, attempts + 1));
    
    try {
      const blobUrl = await getMediaBlobUrl(filename, contentMode === 'ad' ? { dir: 'ads' } : undefined);
      if (blobUrl) {
        setMediaViewerUrl(blobUrl);
        
        // Bei Erfolg Versuche zurücksetzen
        setMediaLoadAttempts(prev => {
          const newMap = new Map(prev);
          newMap.delete(filename);
          return newMap;
        });
        
        // Medientyp basierend auf Dateiendung bestimmen
        const ext = filename.split('.').pop()?.toLowerCase();
        if (ext && ['mp4', 'webm', 'ogg', 'avi', 'mov', 'mkv'].includes(ext)) {
          setMediaViewerType('video');
        } else if (ext && ['mp3', 'wav', 'flac', 'aac'].includes(ext)) {
          setMediaViewerType('audio');
        } else if (ext && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
          setMediaViewerType('image');
        } else {
          setMediaViewerType(null);
        }
        
        setMediaViewerOpen(true);
      }
    } catch (error: any) {
      console.error("Fehler beim Laden der Media-Datei:", error);
      // Zeige die Fehlermeldung vom Server an
      let errorMessage = error.message || t("contentConfig.mediaLoadError");
      
      // Wenn ähnliche Dateien gefunden wurden, füge sie zur Fehlermeldung hinzu
      if (error.similarFiles && error.similarFiles.length > 0) {
        errorMessage += ` Ähnliche Dateien: ${error.similarFiles.join(', ')}`;
      }
      
      setSnackbarMsg(errorMessage);
    }
  };

  // Thumbnail- oder System-Icon-Bild laden (isSystem = System-Modus → SYSTEM_ICON_DIR)
  const loadThumbnail = useCallback(async (filename: string, isSystem?: boolean) => {
    if (!filename) return null;

    const cacheKey = isSystem ? `system:${filename}` : filename;

    if (thumbnailCache.has(cacheKey)) {
      const cachedUrl = thumbnailCache.get(cacheKey)!;
      setThumbnailBlobUrls(prev => {
        if (!prev.has(cacheKey)) {
          return new Map(prev).set(cacheKey, cachedUrl);
        }
        return prev;
      });
      return cachedUrl;
    }

    const currentBlobUrls = thumbnailBlobUrlsRefLocal.current;
    if (currentBlobUrls.has(cacheKey)) {
      return currentBlobUrls.get(cacheKey) || null;
    }

    if (thumbnailErrorCache.has(cacheKey)) {
      return null;
    }

    const cachedAttempts = thumbnailLoadAttemptsCache.get(cacheKey) || 0;
    if (cachedAttempts >= 3) {
      thumbnailErrorCache.add(cacheKey);
      setThumbnailImageErrors(prev => {
        if (!prev.has(cacheKey)) {
          return new Set(prev).add(cacheKey);
        }
        return prev;
      });
      return null;
    }

    const currentAttempts = thumbnailLoadAttemptsRefLocal.current;
    const attempts = currentAttempts.get(cacheKey) || 0;
    if (attempts >= 3) {
      return null;
    }

    const newAttempts = Math.max(attempts, cachedAttempts) + 1;
    setThumbnailLoadAttempts(prev => new Map(prev).set(cacheKey, newAttempts));
    thumbnailLoadAttemptsCache.set(cacheKey, newAttempts);

    try {
      const blobUrl = isSystem
        ? await getSystemIconBlobUrl(filename)
        : await getThumbnailBlobUrl(filename);
      if (blobUrl) {
        thumbnailCache.set(cacheKey, blobUrl);
        setThumbnailBlobUrls(prev => new Map(prev).set(cacheKey, blobUrl));
        setThumbnailLoadAttempts(prev => {
          const newMap = new Map(prev);
          newMap.delete(cacheKey);
          return newMap;
        });
        thumbnailLoadAttemptsCache.delete(cacheKey);
        thumbnailErrorCache.delete(cacheKey);
      }
      return blobUrl;
    } catch (error) {
      console.error(isSystem ? 'Fehler beim Laden des System-Icons:' : 'Fehler beim Laden des Thumbnails:', error);
      if (newAttempts >= 3) {
        thumbnailErrorCache.add(cacheKey);
        setThumbnailImageErrors(prev => {
          if (!prev.has(cacheKey)) {
            return new Set(prev).add(cacheKey);
          }
          return prev;
        });
      }
      return null;
    }
  }, []);

  // Thumbnail laden, wenn sich selectedEntry oder newEntryData ändert
  // Verwendet Refs statt direkter State-Abhängigkeiten, um Endlosschleifen zu vermeiden
  useEffect(() => {
    const currentEntry = newEntryData || selectedEntry;
    if (currentEntry && currentEntry.thumbnail) {
      const filename = String(currentEntry.thumbnail);
      const isSystem = contentMode === "system";
      const key = isSystem ? `system:${filename}` : filename;

      // Prüfe zuerst den globalen Cache
      if (thumbnailCache.has(key)) {
        const cachedUrl = thumbnailCache.get(key)!;
        setThumbnailBlobUrls(prev => {
          if (!prev.has(key)) {
            return new Map(prev).set(key, cachedUrl);
          }
          return prev;
        });
        return;
      }

      // Prüfe Fehler-Cache
      if (thumbnailErrorCache.has(key)) {
        setThumbnailImageErrors(prev => {
          if (!prev.has(key)) {
            return new Set(prev).add(key);
          }
          return prev;
        });
        return;
      }

      const currentBlobUrls = thumbnailBlobUrlsRefLocal.current;
      const currentErrors = thumbnailImageErrorsRefLocal.current;
      const currentAttempts = thumbnailLoadAttemptsRefLocal.current;

      const attempts = currentAttempts.get(key) || 0;
      const cachedAttempts = thumbnailLoadAttemptsCache.get(key) || 0;
      const maxAttempts = Math.max(attempts, cachedAttempts);

      if (filename && !currentBlobUrls.has(key) && !currentErrors.has(key) && maxAttempts < 3 && !thumbnailLoadingCache.has(key)) {
        loadThumbnail(filename, isSystem).catch(err => {
          console.error(isSystem ? 'Fehler beim Laden des System-Icons:' : 'Fehler beim Laden des Thumbnails:', err);
        });
      }
    }
  }, [selectedEntry, newEntryData, contentMode, loadThumbnail]);

  // QR-Code-Bild laden, wenn aktueller Eintrag qr_code hat (nur Inhalte-Modus)
  useEffect(() => {
    if (contentMode !== "content" || generatedQrBlob) return;
    const currentEntry = newEntryData || editingData || selectedEntry;
    const filename = currentEntry?.qr_code;
    if (!filename || typeof filename !== "string" || !filename.trim()) return;
    const key = filename.trim();
    getQrCodeBlobUrl(key).then((url) => {
      if (url) setQrCodeBlobUrls((prev) => (prev.has(key) ? prev : new Map(prev).set(key, url)));
    });
  }, [contentMode, selectedEntry, newEntryData, editingData, generatedQrBlob]);

  // Beim Mount: Globale Cache-Daten in lokale States übernehmen
  useEffect(() => {
    // Thumbnails aus dem Cache in den lokalen State übernehmen
    thumbnailCache.forEach((blobUrl, filename) => {
      if (!thumbnailBlobUrls.has(filename)) {
        setThumbnailBlobUrls(prev => new Map(prev).set(filename, blobUrl));
      }
    });
    
    // Fehler aus dem Cache in den lokalen State übernehmen
    thumbnailErrorCache.forEach(filename => {
      if (!thumbnailImageErrors.has(filename)) {
        setThumbnailImageErrors(prev => new Set(prev).add(filename));
      }
    });
    
    // Versuche aus dem Cache in den lokalen State übernehmen
    thumbnailLoadAttemptsCache.forEach((attempts, filename) => {
      if (!thumbnailLoadAttempts.has(filename) || thumbnailLoadAttempts.get(filename)! < attempts) {
        setThumbnailLoadAttempts(prev => new Map(prev).set(filename, attempts));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur beim Mount ausführen - Dependencies absichtlich weggelassen

  // Cleanup: Blob-URLs NICHT freigeben, da sie im globalen Cache bleiben sollen
  // Die Blob-URLs werden nur freigegeben, wenn die gesamte Anwendung geschlossen wird

  // Attribute für den Randbereich
  const detailAttributes = [
    'entity_number',
    'serial_number',
    'super_category',
    'category',
    'name',
    'file_name',
    'udp_code',
    'thumbnail',
    'show_captions'
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            color="primary"
          >
            {t("contentConfig.createEntry")}
          </Button>
          {SHOW_CONTENT_SYSTEM_TOGGLE && (
            <ToggleButtonGroup
              value={contentMode}
              exclusive
              onChange={(_, newMode: "content" | "system" | "ad" | null) => {
                if (newMode !== null) setContentMode(newMode);
              }}
              aria-label="Inhalte / Bedienfeld / Werbung"
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  padding: "2px 8px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minHeight: "auto",
                  lineHeight: 1.5,
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="content" aria-label={t("contentConfig.contentModeContent")}>
                {t("contentConfig.contentModeContent")}
              </ToggleButton>
              <ToggleButton value="system" aria-label={t("contentConfig.contentModeSystem")}>
                {t("contentConfig.contentModeSystem")}
              </ToggleButton>
              <ToggleButton value="ad" aria-label={t("contentConfig.contentModeAd")}>
                {t("contentConfig.contentModeAd")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {contentMode !== "system" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterDuplicateEntityNumbers}
                  onChange={(e) => setFilterDuplicateEntityNumbers(e.target.checked)}
                  size="small"
                />
              }
              label={t("contentConfig.filterDuplicateEntityNumbers")}
              sx={{ 
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem"
                }
              }}
            />
          )}
          {contentMode !== "ad" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterMissingThumbnails}
                  onChange={(e) => setFilterMissingThumbnails(e.target.checked)}
                  size="small"
                />
              }
              label={contentMode === "system" ? t("contentConfig.filterMissingSystemIcon") : t("contentConfig.filterMissingThumbnails")}
              sx={{ 
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem"
                }
              }}
            />
          )}
          {contentMode !== "system" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterMissingFileName}
                  onChange={(e) => setFilterMissingFileName(e.target.checked)}
                  size="small"
                />
              }
              label={t("contentConfig.filterMissingFileName")}
              sx={{ 
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem"
                }
              }}
            />
          )}
          {SHOW_EXPORT_ICON && (
            <IconButton
              onClick={handleExportToJSON}
              color="primary"
              title={t("contentConfig.exportToCSV") || "Als JSON exportieren"}
              size="small"
              sx={{ 
                ml: 1,
                border: "1px solid",
                borderColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.light",
                  color: "white"
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "0.75rem" // Halbe Größe (Standard ist ~1.5rem)
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {configData.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>{t("contentConfig.noData")}</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 200px)" }}>
          {/* Tabelle links */}
          <Box sx={{ flex: (selectedEntry || newEntryData) ? "1 1 auto" : "1 1 100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                flex: 1, 
                overflow: "auto",
                maxHeight: "100%",
                "&::-webkit-scrollbar": {
                  width: "12px",
                  height: "12px"
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  borderRadius: "6px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.5)"
                  }
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)"
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  {/* Header-Zeile mit Filtern */}
                  <TableRow>
                    <TableCell sx={{ padding: "4px 8px", backgroundColor: "#eeeeee" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                        {t("contentConfig.attributeNames.entity_number")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: "4px 8px", backgroundColor: "#eeeeee" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          {contentMode === "system"
                            ? t("contentConfig.attributeNames.serial_number_s")
                            : t("contentConfig.attributeNames.serial_number")}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: "120px", flex: 1 }}>
                          <Select
                            value={filterGultigkeit}
                            onChange={(e) => setFilterGultigkeit(e.target.value)}
                            displayEmpty
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    fontSize: "0.75rem",
                                    minHeight: "32px",
                                    padding: "4px 12px"
                                  }
                                }
                              }
                            }}
                            sx={{
                              fontSize: "0.75rem",
                              height: "28px",
                              "& .MuiSelect-select": {
                                padding: "2px 8px"
                              }
                            }}
                            renderValue={(selected) => {
                              if (!selected) return <em>{t("contentConfig.all")}</em>;
                              return getGultigkeitDisplay(selected);
                            }}
                          >
                            <MenuItem value="">
                              <em>{t("contentConfig.all")}</em>
                            </MenuItem>
                            {filterValues.gultigkeit.map((value) => (
                              <MenuItem key={value} value={value}>
                                {getGultigkeitDisplay(value)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: "4px 8px", backgroundColor: "#eeeeee" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          {contentMode === "ad"
                            ? t("contentConfig.attributeNames.advertiser")
                            : contentMode === "system"
                            ? t("contentConfig.attributeNames.super_category_s")
                            : t("contentConfig.attributeNames.super_category")}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: "120px", flex: 1 }}>
                          <Select
                            value={filterSuperCategory}
                            onChange={(e) => setFilterSuperCategory(e.target.value)}
                            displayEmpty
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    fontSize: "0.75rem",
                                    minHeight: "32px",
                                    padding: "4px 12px"
                                  }
                                }
                              }
                            }}
                            sx={{
                              fontSize: "0.75rem",
                              height: "28px",
                              "& .MuiSelect-select": {
                                padding: "2px 8px"
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>{t("contentConfig.all")}</em>
                            </MenuItem>
                            {filterValues.superCategory.map((value) => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: "4px 8px", backgroundColor: "#eeeeee" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          {contentMode === "ad"
                            ? t("contentConfig.attributeNames.campaign")
                            : contentMode === "system"
                            ? t("contentConfig.attributeNames.category_s")
                            : t("contentConfig.attributeNames.category")}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: "120px", flex: 1 }}>
                          <Select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            displayEmpty
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    fontSize: "0.75rem",
                                    minHeight: "32px",
                                    padding: "4px 12px"
                                  }
                                }
                              }
                            }}
                            sx={{
                              fontSize: "0.75rem",
                              height: "28px",
                              "& .MuiSelect-select": {
                                padding: "2px 8px"
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>{t("contentConfig.all")}</em>
                            </MenuItem>
                            {filterValues.category.map((value) => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: "4px 8px", backgroundColor: "#eeeeee" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                        {contentMode === "ad" ? t("contentConfig.attributeNames.spot") : t("contentConfig.attributeNames.name")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((entry, index) => {
                    // Prüfe Auswahl mit id oder entity_number + serial_number (eindeutige Kombination)
                    const isSelected = selectedEntry && (
                      (selectedEntry.id !== undefined && selectedEntry.id !== null && entry.id !== undefined && entry.id !== null && selectedEntry.id === entry.id) ||
                      (
                        selectedEntry.entity_number !== undefined && selectedEntry.entity_number !== null && 
                        entry.entity_number !== undefined && entry.entity_number !== null && 
                        String(selectedEntry.entity_number) === String(entry.entity_number) &&
                        // serial_number muss auch übereinstimmen, da mehrere Einträge mit derselben entity_number existieren können
                        (
                          (selectedEntry.serial_number === null || selectedEntry.serial_number === undefined) === (entry.serial_number === null || entry.serial_number === undefined) &&
                          (selectedEntry.serial_number === null || selectedEntry.serial_number === undefined || String(selectedEntry.serial_number) === String(entry.serial_number))
                        )
                      )
                    );
                    // Eindeutige key erstellen: id falls vorhanden, sonst entity_number + serial_number, sonst Index
                    // WICHTIG: serial_number muss einbezogen werden, da mehrere Einträge mit derselben entity_number existieren können
                    const rowKey = entry.id !== undefined && entry.id !== null 
                      ? `entry-${entry.id}` 
                      : (entry.entity_number !== undefined && entry.entity_number !== null 
                          ? `entry-entity-${entry.entity_number}-serial-${entry.serial_number !== null && entry.serial_number !== undefined ? entry.serial_number : 'null'}` 
                          : `entry-index-${index}`);
                    return (
                      <TableRow 
                        key={rowKey}
                        onClick={() => handleRowClick(entry)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isSelected ? "rgba(0, 0, 0, 0.1)" : "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.05)"
                          }
                        }}
                      >
                        <TableCell sx={{ padding: "4px 12px", fontSize: "0.75rem" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {entry.entity_number !== null && entry.entity_number !== undefined ? String(entry.entity_number) : ""}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "4px 12px", fontSize: "0.75rem" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {getGultigkeitDisplay(getGultigkeit(entry))}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "4px 12px", fontSize: "0.75rem" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {contentMode === "ad" && entry.super_category != null && entry.super_category !== ""
                              ? (advertisersList.find((a) => String(a.id) === String(entry.super_category))?.name ?? entry.super_category)
                              : (entry.super_category || "")}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "4px 12px", fontSize: "0.75rem" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {entry.category || ""}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "4px 12px", fontSize: "0.75rem" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {entry.name || ""}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Detailansicht rechts - nur wenn ein Eintrag ausgewählt ist oder ein neuer Eintrag erstellt wird */}
          {(selectedEntry || newEntryData) && (
            <Box sx={{ flexShrink: 0, width: "auto", minWidth: "400px", maxWidth: "600px" }}>
              <Paper sx={{ 
                p: 2, 
                height: "100%", 
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                position: "relative",
                "&::-webkit-scrollbar": {
                  width: "12px",
                  height: "12px"
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  borderRadius: "6px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.5)"
                  }
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)"
              }}>
                {/* Schließen-Button - nur anzeigen, wenn selectedEntry vorhanden ist und nicht im Bearbeiten/Anlegen-Modus */}
                {selectedEntry && !newEntryData && (editingId === null || (selectedEntry.id !== undefined && selectedEntry.id !== null && editingId !== selectedEntry.id)) && (
                  <IconButton
                    onClick={() => setSelectedEntry(null)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      color: "text.secondary",
                      "&:hover": {
                        backgroundColor: "action.hover"
                      }
                    }}
                    size="small"
                    aria-label={t("common.close")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}

                <Typography variant="subtitle1" sx={{ mb: 2, fontSize: "0.75rem", fontWeight: "bold" }}>
                  {newEntryData ? t("contentConfig.newEntry") : t("contentConfig.details")}
                </Typography>

                {/* Hilfsfunktion zum Rendern eines Feldes */}
                {(() => {
                  const isNewEntry = !!newEntryData;
                  const isEditing = selectedEntry && editingId !== null && (
                    selectedEntry.id !== undefined && selectedEntry.id !== null && editingId === selectedEntry.id
                  );
                  const currentData = newEntryData || (isEditing && editingData) || selectedEntry;
                  const isEditMode = isNewEntry || isEditing;
                  const hasCustomTypeOrValue = currentData && (
                    (currentData.custom_type !== null && currentData.custom_type !== undefined && String(currentData.custom_type).trim() !== "") ||
                    (currentData.custom_value !== null && currentData.custom_value !== undefined && String(currentData.custom_value).trim() !== "")
                  );

                  const renderField = (attr: string, position: 'left' | 'center' | 'right') => {
                    const value = currentData ? currentData[attr] : null;
                    // Werbung-Modus: Advertiser / Kampagne / Spot; System-Modus: _s-Pendants
                    const attrLabel = contentMode === "ad"
                      ? (attr === "super_category" ? t("contentConfig.attributeNames.advertiser") : attr === "category" ? t("contentConfig.attributeNames.campaign") : attr === "name" ? t("contentConfig.attributeNames.spot") : t(`contentConfig.attributeNames.${attr}`))
                      : contentMode === "system"
                      ? t(`contentConfig.attributeNames.${attr}_s`)
                      : t(`contentConfig.attributeNames.${attr}`);
                    const isEditable = isNewEntry || isEditing;
                    const isSerialNumber = attr === 'serial_number';
                    const isThumbnail = attr === 'thumbnail';
                    const isFileName = attr === 'file_name';
                    const isShowCaptions = attr === 'show_captions';
                    
                    // Konvertiere true/false zu Ja/Nein für show_captions
                    const getDisplayValue = (val: any): string => {
                      if (isShowCaptions) {
                        if (val === true || val === 'true' || String(val).toLowerCase() === 'true') {
                          return t("common.yes");
                        }
                        if (val === false || val === 'false' || String(val).toLowerCase() === 'false') {
                          return t("common.no");
                        }
                        return "";
                      }
                      return value !== null && value !== undefined ? String(value) : "";
                    };
                    
                    // Konvertiere Ja/Nein zu true/false für show_captions
                    const getBooleanValue = (val: string): boolean | null => {
                      if (val === t("common.yes") || val === "Ja" || val === "Yes") {
                        return true;
                      }
                      if (val === t("common.no") || val === "Nein" || val === "No") {
                        return false;
                      }
                      return null;
                    };

                    // Für Thumbnail: Zeige Bild mit optionaler Caption darunter
                    if (isThumbnail) {
                      const showCaption = currentData && (currentData.show_captions === true || currentData.show_captions === 'true' || String(currentData.show_captions).toLowerCase() === 'true');
                      const titleValue = currentData ? currentData.name : null;
                      
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5, fontSize: "0.75rem" }}>
                            {attrLabel}:
                          </Typography>
                          {isEditable ? (
                            <>
                              <TextField
                                size="small"
                                fullWidth
                                value={
                                  isEditing && !isNewEntry
                                    ? (thumbnailInputValue !== "" ? thumbnailInputValue : (editingData?.thumbnail ? String(editingData.thumbnail) : ""))
                                    : (isNewEntry
                                      ? thumbnailInputValueNewEntry
                                      : (value !== null && value !== undefined ? String(value) : ""))
                                }
                                onChange={(e) => {
                                  if (!isNewEntry) {
                                    setThumbnailInputValue(e.target.value);
                                  } else {
                                    setThumbnailInputValueNewEntry(e.target.value);
                                  }
                                }}
                                onBlur={(e) => {
                                  if (!isNewEntry) {
                                    handleFieldChange(attr, e.target.value);
                                    setThumbnailInputValue(e.target.value);
                                  } else if (isNewEntry && newEntryData) {
                                    setNewEntryData({ ...newEntryData, [attr]: e.target.value });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (!isNewEntry && e.key === 'Enter') {
                                    e.preventDefault();
                                    const inputValue = (e.target as HTMLInputElement).value;
                                    handleFieldChange(attr, inputValue);
                                    setThumbnailInputValue(inputValue);
                                    (e.target as HTMLInputElement).blur();
                                  } else if (isNewEntry && e.key === 'Enter' && newEntryData) {
                                    e.preventDefault();
                                    const inputValue = (e.target as HTMLInputElement).value;
                                    setNewEntryData({ ...newEntryData, [attr]: inputValue });
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                variant="outlined"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px"
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "2px 6px",
                                    fontSize: "0.75rem",
                                    height: "20px"
                                  }
                                }}
                              />
                              {value && (
                                <Box sx={{ mt: 1 }}>
                                  <ThumbnailImageDisplay 
                                    filename={String(value)}
                                    thumbnailBlobUrls={thumbnailBlobUrls}
                                    thumbnailImageErrors={thumbnailImageErrors}
                                    thumbnailLoadAttempts={thumbnailLoadAttempts}
                                    onLoad={(f) => loadThumbnail(f, contentMode === "system")}
                                    onError={(key) => setThumbnailImageErrors(prev => new Set(prev).add(key))}
                                    t={t}
                                    isSystem={contentMode === "system"}
                                  />
                                  {showCaption && titleValue && (
                                    <Typography variant="body2" sx={{ fontSize: "0.75rem", textAlign: "center", mt: 0.5 }}>
                                      {String(titleValue)}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {isEditing && (
                                <Box sx={{ mt: 1, display: "flex", gap: 1, flexDirection: "column" }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    startIcon={<AddIcon />}
                                  >
                                    {contentMode === "system" ? t("contentConfig.uploadThumbnail_s") : t("contentConfig.uploadThumbnail")}
                                    <input
                                      type="file"
                                      hidden
                                      accept="image/*"
                                      onChange={handleThumbnailUpload}
                                    />
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenThumbnailDialog}
                                  >
                                    {contentMode === "system" ? t("contentConfig.selectThumbnail_s") : t("contentConfig.selectThumbnail")}
                                  </Button>
                                  {/* Checkbox für "Titel als Caption" */}
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={currentData ? (currentData.show_captions === true || currentData.show_captions === 'true' || String(currentData.show_captions).toLowerCase() === 'true') : false}
                                        onChange={(e) => {
                                          const boolValue = e.target.checked;
                                          if (isNewEntry && newEntryData) {
                                            setNewEntryData({ ...newEntryData, show_captions: boolValue });
                                          } else {
                                            handleFieldChange('show_captions', boolValue);
                                          }
                                        }}
                                        sx={{
                                          padding: "4px",
                                          "& .MuiSvgIcon-root": {
                                            fontSize: "1rem"
                                          }
                                        }}
                                      />
                                    }
                                    label={t("contentConfig.attributeNames.show_captions")}
                                    sx={{
                                      margin: 0,
                                      marginTop: "8px",
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem"
                                      }
                                    }}
                                  />
                                </Box>
                              )}
                              {isNewEntry && (
                                <Box sx={{ mt: 1, display: "flex", gap: 1, flexDirection: "column" }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    startIcon={<AddIcon />}
                                  >
                                    {contentMode === "system" ? t("contentConfig.uploadThumbnail_s") : t("contentConfig.uploadThumbnail")}
                                    <input
                                      type="file"
                                      hidden
                                      accept="image/*"
                                      onChange={handleThumbnailUpload}
                                    />
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenThumbnailDialog}
                                  >
                                    {contentMode === "system" ? t("contentConfig.selectThumbnail_s") : t("contentConfig.selectThumbnail")}
                                  </Button>
                                  {/* Checkbox für "Titel als Caption" */}
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={currentData ? (currentData.show_captions === true || currentData.show_captions === 'true' || String(currentData.show_captions).toLowerCase() === 'true') : false}
                                        onChange={(e) => {
                                          const boolValue = e.target.checked;
                                          if (newEntryData) {
                                            setNewEntryData({ ...newEntryData, show_captions: boolValue });
                                          }
                                        }}
                                        sx={{
                                          padding: "4px",
                                          "& .MuiSvgIcon-root": {
                                            fontSize: "1rem"
                                          }
                                        }}
                                      />
                                    }
                                    label={t("contentConfig.attributeNames.show_captions")}
                                    sx={{
                                      margin: 0,
                                      marginTop: "8px",
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem"
                                      }
                                    }}
                                  />
                                </Box>
                              )}
                            </>
                          ) : (
                            <>
                              {value && (
                                <Box>
                                  <ThumbnailImageDisplay 
                                    filename={String(value)}
                                    thumbnailBlobUrls={thumbnailBlobUrls}
                                    thumbnailImageErrors={thumbnailImageErrors}
                                    thumbnailLoadAttempts={thumbnailLoadAttempts}
                                    onLoad={(f) => loadThumbnail(f, contentMode === "system")}
                                    onError={(key) => setThumbnailImageErrors(prev => new Set(prev).add(key))}
                                    t={t}
                                    isSystem={contentMode === "system"}
                                  />
                                  {showCaption && titleValue && (
                                    <Typography variant="body2" sx={{ fontSize: "0.75rem", textAlign: "center", mt: 0.5 }}>
                                      {String(titleValue)}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      );
                    }

                    // Für file_name: Zeige Feld mit Buttons
                    if (isFileName) {
                      return (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                            {attrLabel}:
                          </Typography>
                          {isEditable ? (
                            <>
                              <TextField
                                size="small"
                                fullWidth
                                value={value !== null && value !== undefined ? String(value) : ""}
                                onChange={(e) => {
                                  if (isNewEntry && newEntryData) {
                                    setNewEntryData({ ...newEntryData, [attr]: e.target.value });
                                  } else {
                                    handleFieldChange(attr, e.target.value);
                                  }
                                }}
                                variant="outlined"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px"
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "2px 6px",
                                    fontSize: "0.75rem",
                                    height: "20px"
                                  }
                                }}
                              />
                              {isEditing && (
                                <Box sx={{ mt: 1, display: "flex", gap: 1, flexDirection: "column" }}>
                                  {value && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => handleViewMedia(String(value))}
                                    >
                                      {t("contentConfig.viewMedia")}
                                    </Button>
                                  )}
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    startIcon={<AddIcon />}
                                  >
                                    {t("contentConfig.uploadMedia")}
                                    <input
                                      type="file"
                                      hidden
                                      accept="video/*,audio/*,image/*"
                                      onChange={handleMediaUpload}
                                    />
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenMediaDialog}
                                  >
                                    {t("contentConfig.selectMedia")}
                                  </Button>
                                </Box>
                              )}
                              {isNewEntry && (
                                <Box sx={{ mt: 1, display: "flex", gap: 1, flexDirection: "column" }}>
                                  {value && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => handleViewMedia(String(value))}
                                    >
                                      {t("contentConfig.viewMedia")}
                                    </Button>
                                  )}
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    startIcon={<AddIcon />}
                                  >
                                    {t("contentConfig.uploadMedia")}
                                    <input
                                      type="file"
                                      hidden
                                      accept="video/*,audio/*,image/*"
                                      onChange={handleMediaUpload}
                                    />
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenMediaDialog}
                                  >
                                    {t("contentConfig.selectMedia")}
                                  </Button>
                                </Box>
                              )}
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                                {value !== null && value !== undefined ? String(value) : ""}
                              </Typography>
                              {value && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleViewMedia(String(value))}
                                  >
                                    {t("contentConfig.viewMedia")}
                                  </Button>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      );
                    }

                    // Werbung-Modus: Advertiser als Dropdown (Wert = advertisers.id → rc_config.super_category)
                    const isAdvertiserField = contentMode === "ad" && attr === "super_category";
                    if (isAdvertiserField) {
                      const advValue = value !== null && value !== undefined && String(value).trim() !== "" ? String(value) : "";
                      const selectedAdvertiser = advertisersList.find((a) => String(a.id) === advValue);
                      return (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                            {attrLabel}:
                          </Typography>
                          {isEditable ? (
                            <FormControl size="small" fullWidth>
                              <Select
                                value={advValue}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  if (isNewEntry && newEntryData) {
                                    setNewEntryData({ ...newEntryData, [attr]: selectedId === "" ? null : selectedId });
                                  } else {
                                    handleFieldChange(attr, selectedId === "" ? null : selectedId);
                                  }
                                }}
                                variant="outlined"
                                displayEmpty
                                renderValue={(v) => {
                                  const a = advertisersList.find((x) => String(x.id) === v);
                                  return a ? a.name : (v || "");
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": {
                                        fontSize: "0.75rem",
                                        minHeight: "32px",
                                        padding: "4px 12px"
                                      }
                                    }
                                  }
                                }}
                                sx={{
                                  "& .MuiInputBase-root": { height: "28px" },
                                  "& .MuiSelect-select": { padding: "2px 6px", fontSize: "0.75rem", height: "20px" }
                                }}
                              >
                                <MenuItem value="">
                                  <em>—</em>
                                </MenuItem>
                                {advertisersList.map((a) => (
                                  <MenuItem key={a.id} value={String(a.id)}>
                                    {a.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                              {selectedAdvertiser ? selectedAdvertiser.name : (value !== null && value !== undefined ? String(value) : "—")}
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    // Für show_captions: Zeige Select
                    if (isShowCaptions) {
                      return (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                            {attrLabel}:
                          </Typography>
                          {isEditable ? (
                            <FormControl size="small" fullWidth>
                              <Select
                                value={value === true || value === 'true' || String(value).toLowerCase() === 'true' ? t("common.yes") : (value === false || value === 'false' || String(value).toLowerCase() === 'false' ? t("common.no") : "")}
                                onChange={(e) => {
                                  const boolValue = getBooleanValue(e.target.value);
                                  if (isNewEntry && newEntryData) {
                                    setNewEntryData({ ...newEntryData, [attr]: boolValue });
                                  } else {
                                    handleFieldChange(attr, boolValue);
                                  }
                                }}
                                variant="outlined"
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": {
                                        fontSize: "0.75rem",
                                        minHeight: "32px",
                                        padding: "4px 12px"
                                      }
                                    }
                                  }
                                }}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px"
                                  },
                                  "& .MuiSelect-select": {
                                    padding: "2px 6px",
                                    fontSize: "0.75rem",
                                    height: "20px"
                                  }
                                }}
                              >
                                <MenuItem value={t("common.yes")}>{t("common.yes")}</MenuItem>
                                <MenuItem value={t("common.no")}>{t("common.no")}</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                              {getDisplayValue(value)}
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    // Für alle anderen Felder
                    return (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                          {attrLabel}:
                        </Typography>
                        {isEditable ? (
                          <>
                            {isSerialNumber ? (
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={value === null || value === undefined || value === "" ? FOR_ALL_PLAYERS_KEY : String(value)}
                                  onChange={(e) => {
                                    const selectedValue = e.target.value === FOR_ALL_PLAYERS_KEY ? null : e.target.value;
                                    if (isNewEntry && newEntryData) {
                                      setNewEntryData({ ...newEntryData, [attr]: selectedValue });
                                    } else {
                                      handleFieldChange(attr, selectedValue);
                                    }
                                  }}
                                  variant="outlined"
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "0.75rem",
                                          minHeight: "32px",
                                          padding: "4px 12px"
                                        }
                                      }
                                    }
                                  }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "28px"
                                    },
                                    "& .MuiSelect-select": {
                                      padding: "2px 6px",
                                      fontSize: "0.75rem",
                                      height: "20px"
                                    }
                                  }}
                                >
                                  <MenuItem value={FOR_ALL_PLAYERS_KEY}>
                                    {t("contentConfig.forAllPlayers")}
                                  </MenuItem>
                                  {playersWithCustomers.map((player) => (
                                    <MenuItem key={player.sn} value={player.sn}>
                                      {player.sn} ({player.customer_name})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                fullWidth
                                value={value !== null && value !== undefined ? String(value) : ""}
                                onChange={(e) => {
                                  if (isNewEntry && newEntryData) {
                                    setNewEntryData({ ...newEntryData, [attr]: e.target.value });
                                  } else {
                                    handleFieldChange(attr, e.target.value);
                                  }
                                }}
                                variant="outlined"
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px"
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "2px 6px",
                                    fontSize: "0.75rem",
                                    height: "20px"
                                  }
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                            {isSerialNumber ? (
                              (value === null || value === undefined || value === "") 
                                ? t("contentConfig.forAllPlayers")
                                : `${value} (${playerCustomerMap.get(String(value)) || ""})`
                            ) : (
                              value !== null && value !== undefined ? String(value) : ""
                            )}
                          </Typography>
                        )}
                      </Box>
                    );
                  };

                  return (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}>
                      {/* Zeile 1: Eintrag, Gültigkeit, UDP-Code (im System-Modus: Gültigkeit (Feature/Präsentation) an 3. Position) */}
                      <Box sx={{ display: "flex", gap: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Box sx={{ flex: 1 }}>
                          {renderField('entity_number', 'left')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {renderField('serial_number', 'center')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {contentMode === "system" ? renderField('super_category', 'right') : renderField('udp_code', 'right')}
                        </Box>
                      </Box>

                      {/* Zeile 2: Tabreiter/Feature-Präsentation, Abschnitt/Element, Titel (im System-Modus: UDP-Code an 1. Position) */}
                      <Box sx={{ display: "flex", gap: 2, pt: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Box sx={{ flex: 1 }}>
                          {contentMode === "system" ? renderField('udp_code', 'left') : renderField('super_category', 'left')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {renderField('category', 'center')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {renderField('name', 'right')}
                        </Box>
                      </Box>

                      {/* Zeile 2b (nur System-Modus): custom_type, custom_value – im Anzeige-Modus nur bei vorhandenen Werten */}
                      {contentMode === "system" && (isEditMode || hasCustomTypeOrValue) && (
                        <Box sx={{ display: "flex", gap: 2, pt: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                          <Box sx={{ flex: 1 }}>
                            {renderField('custom_type', 'left')}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            {renderField('custom_value', 'right')}
                          </Box>
                        </Box>
                      )}

                      {/* Zeile 3: Thumbnail (entfällt im Werbung-Modus), Dateiname (entfällt im System-Modus) */}
                      <Box sx={{ display: "flex", gap: 4, pt: 2, pb: 2 }}>
                        {contentMode !== "ad" && (
                          <Box sx={{ flex: 1 }}>
                            {renderField('thumbnail', 'left')}
                          </Box>
                        )}
                        {contentMode !== "system" && (
                          <Box sx={{ flex: 1 }}>
                            {renderField('file_name', 'right')}
                          </Box>
                        )}
                      </Box>

                      {/* Zeile 4 (nur Inhalte-Modus, nicht Werbung): QR-Code-Link (links), QR-Code (rechts) */}
                      {contentMode === "content" && (isEditMode || (currentData?.qr_code != null && String(currentData.qr_code).trim() !== "") || (currentData?.qr_code_link != null && String(currentData.qr_code_link).trim() !== "")) && (
                        <Box sx={{ display: "flex", gap: 2, pt: 2, pb: 2, borderTop: "1px solid", borderColor: "divider" }}>
                          {/* Links: qr_code_link, darunter im Bearbeiten-Modus "QR-Code erzeugen" */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5, fontSize: "0.75rem" }}>
                              {t("contentConfig.attributeNames.qr_code_link")}:
                            </Typography>
                            {isEditMode ? (
                              <>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={currentData?.qr_code_link != null && currentData?.qr_code_link !== undefined ? String(currentData.qr_code_link) : ""}
                                  onChange={(e) => {
                                    if (isNewEntry && newEntryData) {
                                      setNewEntryData({ ...newEntryData, qr_code_link: e.target.value });
                                    } else {
                                      handleFieldChange("qr_code_link", e.target.value);
                                    }
                                  }}
                                  variant="outlined"
                                  placeholder="https://..."
                                  sx={{
                                    "& .MuiInputBase-root": { height: "28px" },
                                    "& .MuiInputBase-input": { padding: "2px 6px", fontSize: "0.75rem", height: "20px" }
                                  }}
                                />
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleGenerateQrCode}
                                    disabled={!(currentData?.qr_code_link !== null && currentData?.qr_code_link !== undefined && String(currentData.qr_code_link).trim() !== "")}
                                  >
                                    {t("contentConfig.generateQrCode")}
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-all" }}>
                                {currentData?.qr_code_link != null && currentData?.qr_code_link !== "" ? String(currentData.qr_code_link) : "—"}
                              </Typography>
                            )}
                          </Box>
                          {/* Rechts: qr_code mit Upload/Select; im Anzeigen-Modus nur Vorschaubild (ohne Label/Filename), falls Bild vorhanden */}
                          <Box sx={{ flex: 1 }}>
                            {isEditMode ? (
                              <>
                                <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5, fontSize: "0.75rem" }}>
                                  {t("contentConfig.attributeNames.qr_code")}:
                                </Typography>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={
                                    isEditing && !isNewEntry
                                      ? (qrCodeInputValue !== "" ? qrCodeInputValue : (currentData?.qr_code ? String(currentData.qr_code) : ""))
                                      : (isNewEntry ? qrCodeInputValueNewEntry : (currentData?.qr_code ? String(currentData.qr_code) : ""))
                                  }
                                  onChange={(e) => {
                                    if (isNewEntry && newEntryData) {
                                      setNewEntryData({ ...newEntryData, qr_code: e.target.value });
                                      setQrCodeInputValueNewEntry(e.target.value);
                                    } else {
                                      handleFieldChange("qr_code", e.target.value);
                                      setQrCodeInputValue(e.target.value);
                                    }
                                  }}
                                  variant="outlined"
                                  sx={{
                                    "& .MuiInputBase-root": { height: "28px" },
                                    "& .MuiInputBase-input": { padding: "2px 6px", fontSize: "0.75rem", height: "20px" }
                                  }}
                                />
                                {(currentData?.qr_code || generatedQrPreviewUrl) && (
                                  <Box sx={{ mt: 1 }}>
                                    <img
                                      src={generatedQrPreviewUrl || (currentData?.qr_code ? qrCodeBlobUrls.get(String(currentData.qr_code).trim()) : undefined) || ""}
                                      alt="QR-Code"
                                      style={{ maxWidth: 120, maxHeight: 120, objectFit: "contain" }}
                                    />
                                  </Box>
                                )}
                                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                  <Button variant="outlined" component="label" size="small" startIcon={<AddIcon />}>
                                    {t("contentConfig.uploadQrCode")}
                                    <input type="file" hidden accept="image/*" onChange={handleQrCodeUpload} />
                                  </Button>
                                  <Button variant="outlined" size="small" onClick={handleOpenQrCodeDialog}>
                                    {t("contentConfig.selectQrCode")}
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                {/* Im Anzeigen-Modus: Nur Vorschaubild anzeigen, ohne Label "qr_code" und ohne Dateinamen-Text */}
                                {currentData?.qr_code && qrCodeBlobUrls.get(String(currentData.qr_code).trim()) && (
                                  <Box sx={{ mt: 0 }}>
                                    <img
                                      src={qrCodeBlobUrls.get(String(currentData.qr_code).trim()) || ""}
                                      alt="QR-Code"
                                      style={{ maxWidth: 120, maxHeight: 120, objectFit: "contain" }}
                                    />
                                  </Box>
                                )}
                                {/* Kein Vorschaubild, aber qr_code gesetzt: Dateiname anzeigen (ohne eigenes Label, da Abschnitt nur bei mind. einem Wert sichtbar) */}
                                {currentData?.qr_code && !qrCodeBlobUrls.get(String(currentData.qr_code).trim()) && (
                                  <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                                    {t("contentConfig.attributeNames.qr_code")}: {String(currentData.qr_code)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      )}

                    </Box>
                  );
                })()}

                {/* Buttons unten links */}
                <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
                  {newEntryData ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveNew}
                        size="small"
                        color="primary"
                      >
                        {t("common.save")}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelNew}
                        size="small"
                      >
                        {t("common.cancel")}
                      </Button>
                    </>
                  ) : selectedEntry && editingId !== null && (
                    selectedEntry.id !== undefined && selectedEntry.id !== null && editingId === selectedEntry.id
                  ) ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        size="small"
                        color="primary"
                      >
                        {t("common.save")}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        size="small"
                      >
                        {t("common.cancel")}
                      </Button>
                    </>
                  ) : selectedEntry ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        size="small"
                        color="primary"
                      >
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleDuplicate}
                        size="small"
                        color="primary"
                      >
                        {t("common.duplicate")}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteClick}
                        size="small"
                        color="error"
                      >
                        {t("common.delete")}
                      </Button>
                    </>
                  ) : null}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Lösch-Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("common.delete")}</DialogTitle>
        <DialogContent>
          <Typography>
            {entryToDelete && (
              t("contentConfig.confirmDeleteMessage", { 
                entityNumber: entryToDelete.entity_number || entryToDelete.id || "?",
                gultigkeit: getGultigkeitDisplay(getGultigkeit(entryToDelete))
              })
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media-Auswahl-Dialog - Nur rendern, wenn Dialog geöffnet ist */}
      {mediaDialogOpen && (
        <Dialog open={true} onClose={() => setMediaDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t("contentConfig.selectMedia")}</DialogTitle>
          <DialogContent
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "12px"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                borderRadius: "6px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.5)"
                }
              },
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)"
            }}
          >
            {mediaLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : mediaError ? (
              <Alert severity="error">{mediaError}</Alert>
            ) : availableMediaFiles.length === 0 ? (
              <Typography>{t("contentConfig.noMediaFilesAvailable")}</Typography>
            ) : (
              <List>
                {[...availableMediaFiles].sort((a, b) => a.filename.localeCompare(b.filename)).map((fileInfo) => (
                  <ListItem key={fileInfo.filename} disablePadding>
                    <ListItemButton onClick={() => handleSelectMedia(fileInfo.filename)}>
                      <ListItemText 
                        primary={fileInfo.filename}
                        secondary={formatFileSize(fileInfo.size)}
                        sx={{ ml: 1 }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMediaDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Thumbnail-Auswahl-Dialog - Nur rendern, wenn Dialog geöffnet ist */}
      {thumbnailDialogOpen && (
        <Dialog open={true} onClose={() => setThumbnailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{contentMode === "system" ? t("contentConfig.selectThumbnail_s") : t("contentConfig.selectThumbnail")}</DialogTitle>
          <DialogContent
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "12px"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                borderRadius: "6px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.5)"
                }
              },
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)"
            }}
          >
            {thumbnailLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : thumbnailError ? (
              <Alert severity="error">{thumbnailError}</Alert>
            ) : availableThumbnails.length === 0 ? (
              <Typography>{t("contentConfig.noThumbnailsAvailable")}</Typography>
            ) : (
              <List>
                {[...availableThumbnails].sort().map((filename) => (
                  <ThumbnailListItem
                    key={filename}
                    filename={filename}
                    onSelect={handleSelectThumbnail}
                    onLoad={(f) => loadThumbnail(f, contentMode === "system")}
                  />
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setThumbnailDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* QR-Code-Auswahl-Dialog */}
      {qrCodeDialogOpen && (
        <Dialog open={true} onClose={() => setQrCodeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t("contentConfig.selectQrCode")}</DialogTitle>
          <DialogContent
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)"
            }}
          >
            {availableQrCodes.length === 0 ? (
              <Typography>{t("contentConfig.noThumbnailsAvailable")}</Typography>
            ) : (
              <List>
                {[...availableQrCodes].sort().map((filename) => (
                  <QrCodeListItem
                    key={filename}
                    filename={filename}
                    onSelect={handleSelectQrCode}
                    onLoad={getQrCodeBlobUrl}
                  />
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQrCodeDialogOpen(false)}>{t("common.cancel")}</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Media-Viewer-Dialog */}
      <Dialog 
        open={mediaViewerOpen} 
        onClose={() => {
          setMediaViewerOpen(false);
          if (mediaViewerUrl) {
            URL.revokeObjectURL(mediaViewerUrl);
            setMediaViewerUrl(null);
          }
          setMediaViewerType(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>{t("contentConfig.viewMedia")}</DialogTitle>
        <DialogContent>
          {mediaViewerUrl && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              {mediaViewerType === 'video' && (
                <video
                  src={mediaViewerUrl}
                  controls
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    outline: "none"
                  }}
                />
              )}
              {mediaViewerType === 'audio' && (
                <audio
                  src={mediaViewerUrl}
                  controls
                  style={{
                    width: "100%"
                  }}
                />
              )}
              {mediaViewerType === 'image' && (
                <img
                  src={mediaViewerUrl}
                  alt="Media"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain"
                  }}
                />
              )}
              {!mediaViewerType && (
                <Typography>{t("contentConfig.mediaTypeUnknown")}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMediaViewerOpen(false);
            if (mediaViewerUrl) {
              URL.revokeObjectURL(mediaViewerUrl);
              setMediaViewerUrl(null);
            }
            setMediaViewerType(null);
          }}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbarMsg !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbarMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarMsg(null)}
          severity={snackbarMsg?.includes("Fehler") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentConfigPanel;
